var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { z } from "zod";
import moment from "moment";
import { LibraryDtoTypeEnum, PublisherLegalFormEnum, PublisherOwnerEnum, PublisherContactTypeEnum, PublisherStatusEnum, PublisherRegistrationAgencyEnum, } from "../types/Api";
import { LibrarySystemEnum } from "../types/Library";
import { getAge } from "./getAge";
export var MIN_REPORT_YEAR = 1900;
export var MAX_REPORT_YEAR = 2100;
export var isValidReportYear = function (value) {
    if (!/^\d{4}$/.test(value))
        return false;
    var year = Number(value);
    return (Number.isInteger(year) && year >= MIN_REPORT_YEAR && year <= MAX_REPORT_YEAR);
};
var yearSchema = function (formatMessage) {
    return z.string().refine(isValidReportYear, {
        message: formatMessage({
            id: "ui-br-legaldeposits.outputs.validation.year.invalid",
        }),
    });
};
export var createReportRequestSchema = function (formatMessage) {
    return z.object({
        year: yearSchema(formatMessage),
        libraryId: z.string(),
    });
};
export var createKult1501ReportSchema = createReportRequestSchema;
var DATA_BOX_REGEX = /^[a-zA-Z0-9]{7}$/;
export var isValidEmail = function (value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
};
export var createLibrarySchema = function (formatMessage) {
    return z.object({
        name: z.string().min(1, {
            message: formatMessage({
                id: "ui-br-legaldeposits.validation.required",
            }),
        }),
        type: z.nativeEnum(LibraryDtoTypeEnum, {
            errorMap: function () { return ({
                message: formatMessage({
                    id: "ui-br-legaldeposits.validation.required",
                }),
            }); },
        }),
        regionId: z.string().nullable().optional(),
        nonPeriodicalsCountAllRegions: z.preprocess(function (val) { return (val === "" || val == null ? 0 : Number(val)); }, z
            .number()
            .int()
            .min(0, {
            message: formatMessage({
                id: "ui-br-legaldeposits.libraries.validation.nonPeriodicalsAllRegions.min",
            }),
        })
            .max(2, {
            message: formatMessage({
                id: "ui-br-legaldeposits.libraries.validation.nonPeriodicalsAllRegions.max",
            }),
        })),
        nonPeriodicalsCountOwnRegion: z.preprocess(function (val) { return (val === "" || val == null ? 0 : Number(val)); }, z
            .number()
            .int()
            .min(0, {
            message: formatMessage({
                id: "ui-br-legaldeposits.libraries.validation.nonPeriodicalsOwnRegion.min",
            }),
        })
            .max(1, {
            message: formatMessage({
                id: "ui-br-legaldeposits.libraries.validation.nonPeriodicalsOwnRegion.max",
            }),
        })),
        periodicalsCount: z.preprocess(function (val) { return (val === "" || val == null ? 0 : Number(val)); }, z
            .number()
            .int()
            .min(0, {
            message: formatMessage({
                id: "ui-br-legaldeposits.libraries.validation.periodicalsCount.min",
            }),
        })
            .max(2, {
            message: formatMessage({
                id: "ui-br-legaldeposits.libraries.validation.periodicalsCount.max",
            }),
        })),
        oaiUrlMonography: z
            .string()
            .optional()
            .refine(function (val) { return !val || /^https?:\/\/[^\s]+$/.test(val); }, {
            message: formatMessage({
                id: "ui-br-legaldeposits.libraries.validation.oaiUrl",
            }),
        }),
        canRequestCopies: z.boolean().default(false),
        includeReferentInSignature: z.boolean().default(false),
        remindersEnabled: z.boolean().default(true),
        defaultReminderOffsetDays: z.preprocess(function (val) { return (val === "" || val == null ? 0 : Number(val)); }, z
            .number()
            .int()
            .min(0, {
            message: formatMessage({
                id: "ui-br-legaldeposits.libraries.validation.defaultReminderOffsetDays.min",
            }),
        })
            .max(365, {
            message: formatMessage({
                id: "ui-br-legaldeposits.libraries.validation.defaultReminderOffsetDays.max",
            }),
        })),
        defaultMinGapDays: z.preprocess(function (val) { return (val === "" || val == null ? 0 : Number(val)); }, z
            .number()
            .int()
            .min(1, {
            message: formatMessage({
                id: "ui-br-legaldeposits.libraries.validation.defaultMinGapDays.min",
            }),
        })
            .max(365, {
            message: formatMessage({
                id: "ui-br-legaldeposits.libraries.validation.defaultMinGapDays.max",
            }),
        })),
        correspondenceContact: z.string().optional(),
        librarySystem: z.nativeEnum(LibrarySystemEnum, {
            errorMap: function () { return ({
                message: formatMessage({
                    id: "ui-br-legaldeposits.validation.required",
                }),
            }); },
        }),
    });
};
var createContactSchema = function (formatMessage) {
    return z
        .object({
        id: z.string().nullable().optional(),
        type: z.nativeEnum(PublisherContactTypeEnum, {
            errorMap: function () { return ({
                message: formatMessage({
                    id: "ui-br-legaldeposits.validation.required",
                }),
            }); },
        }),
        value: z.string().min(1, {
            message: formatMessage({
                id: "ui-br-legaldeposits.validation.required",
            }),
        }),
        info: z.string().nullable().optional(),
        isPrimary: z.boolean().optional(),
    })
        .superRefine(function (contact, ctx) {
        if (contact.type === PublisherContactTypeEnum.EMAIL) {
            if (!isValidEmail(contact.value)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "ui-br-legaldeposits.publishers.validation.contact.email",
                    }),
                    path: ["value"],
                });
            }
        }
        else if (contact.type === PublisherContactTypeEnum.WEB) {
            if (!/^https?:\/\/[^\s$.?#].[^\s]*$/.test(contact.value)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "ui-br-legaldeposits.publishers.validation.contact.web",
                    }),
                    path: ["value"],
                });
            }
        }
        else if (contact.type === PublisherContactTypeEnum.PHONE) {
            var input = contact.value.trim();
            var hasPlus = input.startsWith("+");
            var digitsOnly = input.replace(/\D/g, "");
            var hasInvalidChars = /[a-zA-Z]/.test(input);
            if (hasInvalidChars) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "ui-br-legaldeposits.publishers.validation.contact.phone",
                    }),
                    path: ["value"],
                });
            }
            else if (digitsOnly.length < 9) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "ui-br-legaldeposits.publishers.validation.contact.phone",
                    }),
                    path: ["value"],
                });
            }
            else if (digitsOnly.length === 9) {
                var formatted = "".concat(digitsOnly.slice(0, 3), " ").concat(digitsOnly.slice(3, 6), " ").concat(digitsOnly.slice(6));
                if (contact.value !== formatted) {
                    contact.value = formatted;
                }
            }
            else if (hasPlus && digitsOnly.length >= 10) {
                if (digitsOnly.length > 15) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: formatMessage({
                            id: "ui-br-legaldeposits.publishers.validation.contact.phone.e164",
                        }),
                        path: ["value"],
                    });
                }
                else {
                    var normalized = "+".concat(digitsOnly);
                    if (contact.value !== normalized) {
                        contact.value = normalized;
                    }
                }
            }
            else {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "ui-br-legaldeposits.publishers.validation.contact.phone",
                    }),
                    path: ["value"],
                });
            }
        }
        else if (contact.type === PublisherContactTypeEnum.DATA_BOX) {
            if (!DATA_BOX_REGEX.test(contact.value)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "ui-br-legaldeposits.publishers.validation.contact.dataBox",
                    }),
                    path: ["value"],
                });
            }
        }
    });
};
export var createPublisherContactsSchema = function (formatMessage) {
    return z
        .object({
        contacts: z.array(createContactSchema(formatMessage)),
        syncedContacts: z.array(z.object({
            id: z.string().nullable().optional(),
            type: z.nativeEnum(PublisherContactTypeEnum).optional(),
            value: z.string().optional(),
            info: z.string().nullable().optional(),
            isPrimary: z.boolean().optional(),
            source: z.string().optional(),
            sourceId: z.string().nullable().optional(),
        })),
    })
        .superRefine(function (data, ctx) {
        var allContacts = __spreadArray(__spreadArray([], data.contacts.map(function (c) { return (__assign({}, c)); }), true), data.syncedContacts.map(function (c) { return (__assign({}, c)); }), true);
        var contactsByType = allContacts.reduce(function (acc, contact) {
            if (contact.type) {
                if (!acc[contact.type]) {
                    acc[contact.type] = [];
                }
                acc[contact.type].push(contact);
            }
            return acc;
        }, {});
        var hasMultiplePrimary = Object.values(contactsByType).some(function (contacts) { return contacts.filter(function (c) { return c.isPrimary; }).length > 1; });
        if (hasMultiplePrimary) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: formatMessage({
                    id: "ui-br-legaldeposits.publishers.validation.contact.onePrimaryPerType",
                }),
                path: ["contacts"],
            });
        }
    });
};
var createAddressSchema = function (formatMessage) {
    return z.object({
        addressCode: z
            .string()
            .default("")
            .transform(function (val) { return (val && val.trim() === "" ? "" : val); }),
        street: z.string().min(1, {
            message: formatMessage({
                id: "ui-br-legaldeposits.publishers.validation.address.street",
            }),
        }),
        municipality: z.string().min(1, {
            message: formatMessage({
                id: "ui-br-legaldeposits.publishers.validation.address.municipality",
            }),
        }),
        postalCode: z.string().min(1, {
            message: formatMessage({
                id: "ui-br-legaldeposits.publishers.validation.address.postalCode",
            }),
        }),
        regionId: z.string().min(1, {
            message: formatMessage({
                id: "ui-br-legaldeposits.validation.required",
            }),
        }),
        region: z
            .object({
            id: z.string().min(1, {
                message: formatMessage({
                    id: "ui-br-legaldeposits.publishers.validation.address.region",
                }),
            }),
            name: z.string().optional(),
        })
            .optional(),
    });
};
export var createNewPublisherSchema = function (formatMessage) {
    return z
        .object({
        legalForm: z.nativeEnum(PublisherLegalFormEnum, {
            required_error: formatMessage({
                id: "ui-br-legaldeposits.publishers.validation.legalForm",
            }),
        }),
        registrationAgency: z.nativeEnum(PublisherRegistrationAgencyEnum, {
            errorMap: function () { return ({
                message: formatMessage({
                    id: "ui-br-legaldeposits.publishers.validation.registrationAgency",
                }),
            }); },
        }),
        version: z.number().optional(),
        id: z.string().optional(),
        nameSuffix: z.string().optional(),
        name: z.string().min(1, {
            message: formatMessage({
                id: "ui-br-legaldeposits.publishers.validation.name",
            }),
        }),
        status: z.nativeEnum(PublisherStatusEnum, {
            required_error: "Status is required",
        }),
        companyNumber: z
            .string()
            .optional()
            .transform(function (val) { return (val ? val.trim() : val); })
            .refine(function (val) { return !val || val === "" || val.length === 8; }, {
            message: formatMessage({
                id: "ui-br-legaldeposits.publishers.validation.companyNumber",
            }),
        }),
        taxNumber: z
            .union([
            z.literal(""),
            z.string().refine(function (val) { return /^[A-Za-z]{2}\d{2,13}$/.test(val); }, {
                message: formatMessage({
                    id: "ui-br-legaldeposits.publishers.validation.taxNumber",
                }),
            }),
        ])
            .optional(),
        birthDate: z
            .union([
            z.literal(""),
            z
                .string()
                .refine(function (val) { return moment(val, "DD/MM/YYYY", true).isValid(); }, {
                message: formatMessage({
                    id: "ui-br-legaldeposits.publishers.validation.birthDate.format",
                }),
            }),
        ])
            .optional()
            .transform(function (val) {
            if (!val || val === "")
                return "";
            var parsed = moment(val, "DD/MM/YYYY", true);
            return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
        })
            .superRefine(function (val, ctx) {
            if (!val)
                return;
            var m = moment(val, "YYYY-MM-DD", true);
            if (!m.isValid())
                return;
            var min = moment("1900-01-01", "YYYY-MM-DD", true);
            var max = moment().endOf("day");
            if (m.isBefore(min)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "ui-br-legaldeposits.publishers.validation.birthDate.min",
                    }),
                    path: [],
                });
            }
            if (m.isAfter(max)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "ui-br-legaldeposits.publishers.validation.birthDate.max",
                    }),
                    path: [],
                });
            }
        }),
        mainAddress: z.object({
            addressCode: z
                .string()
                .default("")
                .transform(function (val) { return (val && val.trim() === "" ? "" : val); }),
            street: z.string().optional().default(""),
            municipality: z.string().optional().default(""),
            postalCode: z.string().optional().default(""),
            regionId: z.string().min(1, {
                message: formatMessage({
                    id: "ui-br-legaldeposits.validation.required",
                }),
            }),
            region: z
                .object({
                id: z.string().min(1, {
                    message: formatMessage({
                        id: "ui-br-legaldeposits.publishers.validation.address.region",
                    }),
                }),
                name: z.string().optional(),
            })
                .optional(),
        }),
        contacts: z.array(createContactSchema(formatMessage)).optional(),
    })
        .superRefine(function (data, ctx) {
        if (data.legalForm === PublisherLegalFormEnum.NATURAL &&
            data.birthDate &&
            typeof data.birthDate === "string" &&
            data.birthDate.trim() !== "") {
            var age = getAge(data.birthDate);
            if (age < 18) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "ui-br-legaldeposits.publishers.validation.birthDate.age",
                    }, { age: age }),
                    path: ["birthDate"],
                });
            }
        }
    });
};
export var createPublisherFormSchema = function (formatMessage) {
    return z
        .object({
        owner: z.nativeEnum(PublisherOwnerEnum).optional(),
        name: z.string().min(2, {
            message: formatMessage({
                id: "ui-br-legaldeposits.publishers.validation.name.min",
            }),
        }),
        nameSuffix: z.string().optional(),
        legalForm: z.nativeEnum(PublisherLegalFormEnum, {
            errorMap: function () { return ({
                message: formatMessage({
                    id: "ui-br-legaldeposits.validation.required",
                }),
            }); },
        }),
        companyNumber: z
            .string()
            .regex(/^[0-9]{8}$/, {
            message: formatMessage({
                id: "ui-br-legaldeposits.publishers.validation.companyNumber.format",
            }),
        })
            .optional()
            .or(z.literal("")),
        taxNumber: z.string().optional(),
        birthDate: z.string().nullable().optional(),
        mainAddress: createAddressSchema(formatMessage),
        mailingAddress: createAddressSchema(formatMessage).nullable().optional(),
        contacts: z.array(createContactSchema(formatMessage)),
    })
        .superRefine(function (data, ctx) {
        var contactsByType = data.contacts.reduce(function (acc, contact) {
            if (contact.type) {
                if (!acc[contact.type]) {
                    acc[contact.type] = [];
                }
                acc[contact.type].push(contact);
            }
            return acc;
        }, {});
        var hasMultiplePrimary = Object.values(contactsByType).some(function (contacts) { return contacts.filter(function (c) { return c.isPrimary; }).length > 1; });
        if (hasMultiplePrimary) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: formatMessage({
                    id: "ui-br-legaldeposits.publishers.validation.contact.onePrimaryPerType",
                }),
                path: ["contacts"],
            });
        }
    });
};
export var createTitleVerificationSchema = function (formatMessage) {
    return z.object({
        publisherId: z.string().min(1, {
            message: formatMessage({
                id: "ui-br-legaldeposits.title.validation.publisherRequired",
            }),
        }),
    });
};
// Publisher prefix validation
export var PUBLISHER_PREFIX_MIN_LENGTH = 6;
var PUBLISHER_PREFIX_PATTERN = /^[0-9-]+$/;
export var validatePublisherPrefix = function (prefix) {
    var trimmed = prefix.trim();
    var isEmpty = trimmed.length === 0;
    var isTooShort = !isEmpty && trimmed.length < PUBLISHER_PREFIX_MIN_LENGTH;
    var hasInvalidChars = !isEmpty && !PUBLISHER_PREFIX_PATTERN.test(trimmed);
    return {
        isValid: isEmpty || (!isTooShort && !hasInvalidChars),
        trimmed: trimmed,
        isTooShort: isTooShort,
        hasInvalidChars: hasInvalidChars,
    };
};
